import type { CSSProperties, JSX } from 'react';
import type { ShellHydration } from '../../../api.js';
import type { Palette } from '../../../_theme/palette.js';
import { hexWithAlpha } from '../../../_theme/palette.js';
import type { SurveyQuestion } from '../types.js';
import { QuestionField } from './QuestionField.js';
import { QuestionMediaBlock } from './QuestionMediaBlock.js';

interface ConversationalQuestionProps {
  palette: Palette;
  question: SurveyQuestion;
  questionNumber: number;
  value: unknown;
  hydration: ShellHydration;
  onChange: (value: unknown) => void;
  onConfirm: () => void | Promise<void>;
  submitting: boolean;
  isLast: boolean;
  // True when an auto-advance timer is running for this question. The OK
  // button shows a soft pulse + "Continuing →" hint instead of "press Enter"
  // so the user sees the survey is about to move on.
  advancePending?: boolean;
  error: string | null;
}

export function ConversationalQuestion({
  palette,
  question,
  questionNumber,
  value,
  hydration,
  onChange,
  onConfirm,
  submitting,
  isLast,
  advancePending,
  error,
}: ConversationalQuestionProps): JSX.Element {
  const showNumber = question.showNumber !== false;
  return (
    <div style={wrapperStyle()}>
      {showNumber && <QuestionNumberBadge palette={palette} number={questionNumber} />}
      <h2 style={titleStyle(palette)}>
        {question.label}
        {question.required && <span style={{ color: '#ef4444', marginLeft: 6 }}>*</span>}
      </h2>
      {question.description && <p style={descriptionStyle(palette)}>{question.description}</p>}
      {question.media && <QuestionMediaBlock media={question.media} radius={palette.radius} />}
      <div style={answerSlotStyle()}>
        <QuestionField
          palette={palette}
          question={question}
          value={value}
          onChange={onChange}
          hydration={hydration}
        />
      </div>
      {error && <p style={errorStyle()}>{error}</p>}
      <div style={actionsRowStyle()}>
        <button
          type="button"
          onClick={() => void onConfirm()}
          disabled={submitting}
          className="chirp-btn"
          style={okButtonStyle(palette, submitting, advancePending)}
        >
          <span>{submitting ? 'Sending…' : isLast ? 'Submit' : 'OK'}</span>
          {!submitting && <CheckIcon />}
        </button>
        {!submitting && (
          <span style={hintStyle(palette, advancePending)}>
            {advancePending ? (
              <>Continuing <ArrowIcon /></>
            ) : (
              <>press <kbd style={kbdStyle(palette)}>Enter</kbd> ↵</>
            )}
          </span>
        )}
      </div>
    </div>
  );
}

function QuestionNumberBadge({ palette, number }: { palette: Palette; number: number }): JSX.Element {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: palette.primary, fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
      <span>{number}</span>
      <span aria-hidden="true">›</span>
    </div>
  );
}

function CheckIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function ArrowIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

function wrapperStyle(): CSSProperties {
  return { display: 'flex', flexDirection: 'column', maxWidth: 560, margin: '0 auto', width: '100%' };
}

function titleStyle(palette: Palette): CSSProperties {
  return { margin: 0, fontSize: 26, fontWeight: 600, color: palette.text, lineHeight: 1.25 };
}

function descriptionStyle(palette: Palette): CSSProperties {
  return { margin: '12px 0 0', fontSize: 15, color: palette.textMuted, lineHeight: 1.6 };
}

function answerSlotStyle(): CSSProperties {
  return { marginTop: 28 };
}

function actionsRowStyle(): CSSProperties {
  return { marginTop: 28, display: 'flex', alignItems: 'center', gap: 16 };
}

function okButtonStyle(palette: Palette, submitting: boolean, advancePending?: boolean): CSSProperties {
  // When an auto-advance is pending, surround the OK button with a soft
  // accent halo that pulses gently — matches the Typeform "we got your
  // answer, moving on" cue. The button stays clickable for users who want
  // to confirm immediately.
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 22px',
    borderRadius: 10,
    border: 'none',
    background: palette.primary,
    color: palette.onPrimary,
    fontSize: 14,
    fontWeight: 600,
    cursor: submitting ? 'not-allowed' : 'pointer',
    opacity: submitting ? 0.7 : 1,
    transition: 'box-shadow 200ms ease, transform 200ms ease',
    boxShadow: advancePending ? `0 0 0 4px ${hexWithAlpha(palette.primary, 0.22)}` : 'none',
    transform: advancePending ? 'scale(1.02)' : 'scale(1)',
  };
}

function hintStyle(palette: Palette, advancePending?: boolean): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 12,
    color: advancePending ? palette.primary : palette.textMuted,
    fontWeight: advancePending ? 500 : 400,
    transition: 'color 200ms ease',
  };
}

function kbdStyle(palette: Palette): CSSProperties {
  return {
    padding: '2px 6px',
    border: `1px solid ${palette.border}`,
    borderRadius: 4,
    fontFamily: 'inherit',
    fontSize: 11,
    fontWeight: 600,
    color: palette.text,
    background: palette.card,
  };
}

function errorStyle(): CSSProperties {
  return { margin: '18px 0 0', color: '#ef4444', fontSize: 13 };
}
